import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactElement, ReactNode } from 'react';
import { useRouterState } from '@tanstack/react-router';
import { useTranslation } from "react-i18next";
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined';

import {
  AccountBoxIcon,
  AdminIcon,
  CrewIcon,
  DashboardIcon,
  HomeIcon,
  ShippingIcon,
} from '@src/shared/ui/icons';

const SIDEBAR_COLLAPSED_STORAGE_KEY = 'sidebar:collapsed';

function getStoredSidebarCollapsedState() {
  const value = window.localStorage.getItem(SIDEBAR_COLLAPSED_STORAGE_KEY);

  if (value === 'true') return true;
  if (value === 'false') return false;

  return true;
}

type SidebarLeafItem = {
  key: string;
  label: string;
  to: string;
  icon?: ReactElement;
  children?: never;
};

type SidebarGroupItem = {
  key: string;
  label: string;
  icon?: ReactElement;
  children: SidebarLeafItem[];
  to?: never;
};

export type SidebarItem = SidebarLeafItem | SidebarGroupItem;

type SidebarItems = {
  top: SidebarItem[];
  bottom: SidebarItem[];
};

type SidebarContextValue = {
  isCollapsed: boolean;
  isExpanded: boolean;
  open: () => void;
  collapse: () => void;
  toggle: () => void;
  setHovered: (value: boolean) => void;
  items: SidebarItems;
};

const SidebarContext = React.createContext<SidebarContextValue | null>(null);

type SidebarProviderProps = {
  children: ReactNode;
};

function SidebarProvider({ children }: SidebarProviderProps) {
  const { t } = useTranslation();
  const [isCollapsed, setIsCollapsed] = useState(getStoredSidebarCollapsedState);
  const [isHovered, setIsHovered] = useState(false);
  const ignoringHoverRef = useRef(false);

  const isExpanded = !isCollapsed || isHovered;

  const startIgnoringHover = useCallback(() => {
    ignoringHoverRef.current = true;
    setTimeout(() => { ignoringHoverRef.current = false; }, 400);
  }, []);

  const open = useCallback(() => {
    setIsCollapsed(false);
  }, []);

  const collapse = useCallback(() => {
    setIsCollapsed(true);
    setIsHovered(false);
    startIgnoringHover();
  }, [startIgnoringHover]);

  const toggle = useCallback(() => {
    setIsCollapsed((prevValue) => {
      if (!prevValue) {
        setIsHovered(false);
        startIgnoringHover();
      }
      return !prevValue;
    });
  }, [startIgnoringHover]);

  const setHovered = useCallback((value: boolean) => {
    if (value && ignoringHoverRef.current) return;
    setIsHovered(value);
  }, []);

  const pathname = useRouterState({ select: (s) => s.location.pathname });

  // biome-ignore lint/correctness/useExhaustiveDependencies: collapse hover state after route changes
  useEffect(() => {
    if (isCollapsed) {
      setIsHovered(false);
    }
  }, [pathname, isCollapsed]);

  useEffect(() => {
    window.localStorage.setItem(
      SIDEBAR_COLLAPSED_STORAGE_KEY,
      String(isCollapsed),
    );
  }, [isCollapsed]);

  //  ToDo: permissions
  //  const { user } = useAuthentication();
  //  const canViewUserList = hasPermissions(user, ['users.kc_view_user_list']);
  const canViewUserList = true;
  const top = useMemo(
    () => {
      const adminChildren: SidebarLeafItem[] = [
        {
          key: 'absence',
          label: t('sidebar.absence'),
          to: '/absence',
        },
      ];

      if (canViewUserList) {
        adminChildren.push({
          key: 'users',
          label: t('sidebar.user_management', 'User management'),
          to: '/users',
        });
      }

      return [
        {
          key: 'home',
          label: t('sidebar.home', 'Home'),
          to: '/',
          icon: <HomeIcon color="inherit" />,
        },
        {
          key: 'dashboard',
          label: t('sidebar.dashboard', 'Dashboard'),
          to: '/dashboard',
          icon: <DashboardIcon color="inherit" />,
        },
        {
          key: 'admin',
          label: t('sidebar.admin'),
          icon: <AdminIcon color="inherit" />,
          children: adminChildren,
        },
        {
          key: 'clients',
          label: t('sidebar.clients'),
          to: '/clients',
          icon: <AccountBoxIcon color="inherit" />,
        },
        {
          key: 'fleet',
          label: t('sidebar.fleet', 'Fleet'),
          to: '/fleet',
          icon: <ShippingIcon color="inherit" />,
        },
        {
          key: 'team',
          label: t('sidebar.team'),
          to: '/team',
          icon: <CrewIcon color="inherit" />,
        },
      ];
    },
    [t],
  );

  const bottom = useMemo(
    () => [
      {
        key: 'logout',
        label: t('sidebar.logout'),
        to: '/auth/logout',
        icon: <LogoutOutlinedIcon color="inherit" />,
      },
    ],
    [t],
  );

  const contextValue = useMemo(
    () => ({
      isCollapsed,
      isExpanded,
      open,
      collapse,
      toggle,
      setHovered,
      items: {
        top,
        bottom,
      },
    }),
    [bottom, collapse, isCollapsed, isExpanded, open, setHovered, toggle, top],
  );

  return (
    <SidebarContext.Provider value={contextValue}>
      {children}
    </SidebarContext.Provider>
  );
}

SidebarProvider.displayName = 'SidebarProvider';
// biome-ignore lint/style/useComponentExportOnlyModules: context and provider intentionally share this module
export { SidebarContext, SidebarProvider };
